
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import DNSRepositoryInterface, {
  DNSRecordsInterface,
  DNSZonesInterface,
} from '@/services/DNS/Interfaces';
import DeleteDnsRecordDialog from '@/components/dialogs/dns/DeleteDnsRecordDialog.vue';
import SyncDNSDialog from '@/components/dialogs/dns/SyncDNSDialog.vue';

@Component({
  inject: ['dnsRepository'],
  components: {
    DeleteDnsRecordDialog,
    SyncDNSDialog,
  },
})
export default class WebsiteDnsPanel extends Vue {
  @Prop({ required: true }) private readonly domain!: string;
  @Prop({ required: true }) private readonly websiteId!: number;

  private dnsRepository!: DNSRepositoryInterface;

  private loading: boolean = true;
  private search: string = '';
  private dnsZone: DNSZonesInterface | null = null;
  private dnsRecords: DNSRecordsInterface[] = [];

  private deleteDnsRecordDialog: string = '';
  private deleteDnsRecordDialogData: DNSRecordsInterface | false = false;
  private syncDNSDialog: string = '';
  private domainForSyncDialog: string = '';

  private headers: DataTableHeader[] = [
    { text: 'Host', value: 'content.host' },
    { text: 'Type', value: 'type' },
    { text: 'Priority', value: 'content.priority' },
    { text: 'Value', value: 'displayValue' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public get dnsRecordsForTable() {
    return this.dnsRecords.map((record) => ({
      ...record,
      displayValue: this.getRecordDisplayValue(record),
    }));
  }

  public async created(): Promise<void> {
    try {
      await this.loadZoneAndRecords();
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      this.loading = false;
    }
  }

  private async loadZoneAndRecords(): Promise<void> {
    const zones = await this.dnsRepository.searchZones(this.domain);
    this.dnsZone = zones.find((zone) => zone.websiteId === this.websiteId)
      || zones.find((zone) => zone.installation?.domain === this.domain || zone.domain === this.domain)
      || zones[0]
      || null;

    if (!this.dnsZone) {
      return;
    }

    this.dnsRecords = await this.dnsRepository.getDNSZoneRecords(this.dnsZone.id);
  }

  private getRecordDisplayValue(record: DNSRecordsInterface): string {
    const content = record.content;

    if (record.type === 'SOA') {
      return [
        content.target,
        content.responsible_domain,
        content.serial_number,
        content.refresh_interval,
        content.retry_interval,
        content.expire_interval,
        content.neg_cache_ttl,
      ]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .join(' ');
    }

    if (record.type === 'CAA') {
      return [
        content.flag,
        content.tag,
        content.target ? `${content.target}` : '',
      ]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .join(' ');
    }

    return content?.target || record.target || '';
  }

  private async refreshRecords(): Promise<void> {
    if (!this.dnsZone) {
      return;
    }

    try {
      this.loading = true;
      this.dnsRecords = await this.dnsRepository.getDNSZoneRecords(this.dnsZone.id);
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    } finally {
      this.loading = false;
    }
  }

  private createRecord(): void {
    if (!this.dnsZone) {
      return;
    }

    this.$router.push({ name: 'dns_record_create', params: { id: String(this.dnsZone.id) } });
  }

  private deleteRecordPrompt(record: DNSRecordsInterface): void {
    this.deleteDnsRecordDialog = 'delete-dns-record-dialog';
    this.deleteDnsRecordDialogData = record;
  }

  private async deleteRecord(record: DNSRecordsInterface): Promise<void> {
    if (!this.dnsZone) {
      return;
    }

    await this.dnsRepository.deleteDNSRecord(this.dnsZone.id, record.id);
    await this.refreshRecords();
    this.deleteDnsRecordDialog = '';
    this.deleteDnsRecordDialogData = false;
  }

  private showSyncDialog(): void {
    if (!this.dnsZone) {
      return;
    }

    this.domainForSyncDialog = this.dnsZone.installation.domain;
    this.syncDNSDialog = 'SyncDNSDialog';
  }

  private async syncDNS(): Promise<void> {
    try {
      await this.dnsRepository.syncDNS(this.domainForSyncDialog, undefined, 0);
      this.$successDialog.show('DNS sync started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }
}
